import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import api from '@api';
import './App.css';
import { Helmet } from 'react-helmet';
import { useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar.jsx';
import Footer from './components/Footer.jsx';
import PublicLayout from './layouts/publicLayout.jsx';
import DynamicOrgRegistration from './components/DynamicOrgRegistration';
import DynamicOrgLeagueInfo from './components/DynamicOrgLeagueInfo';
import DynamicOrgTournament from './components/DyanamicOrgTournament';
import DynamicOrgOurTeam from './components/DynamicOrgOurTeam';
import DynamicOrgLeagueFootage from './components/DynamicOrgLeagueFootage';
import DynamicOrgYouth from './components/DynamicOrgYouth';
import DynamicOrgContact from './components/DynamicOrgContact';
import DynamicOrgLeaderboard from './components/DynamicOrgLeaderboard';
import DynamicOrgPlayers from './components/DynamicOrgPlayers';
import DynamicOrgGames from './components/DynamicOrgGames';
import Home from './pages/Home.jsx';
import Login from './pages/Login.jsx';
import UserHomepage from './pages/UserHomepage.jsx';
import Frontend from './pages/Frontend.jsx';
import Organizations from './pages/Organizations.jsx';
import LeagueList from './pages/League/LeagueList.jsx';
import League from './pages/League/Auth/LeagueAuth.jsx';
import TeamList from './pages/Team/TeamList.jsx';
import Team from './pages/Team/Team.jsx';
import PlayerList from './pages/Player/PlayerList.jsx';
import Player from './pages/Player/Player.jsx';
import PlayerStats from './pages/Player/Public/PlayerStats.jsx';
import GamePage from './pages/Game/GamePage.jsx';
import SchedulePage from './pages/Schedule/SchedulePage.jsx';
import GamePeriod from './pages/GamePeriod/GamePeriod.jsx';
import UserSettings from './pages/UserSettingsPage.jsx';
import PlayerAttributes from './pages/PlayerAttribute/PlayerAttributes.jsx';
import TeamAuth from './pages/Team/Auth/TeamAuth.jsx';
import ScheduleBuilder from './pages/ScheduleBuilder/ScheduleBuilder.jsx';
import BusinessPage from './pages/BusinessPage.jsx';
import Browse from './pages/Browse.jsx';
import Seasons from './pages/Season/Auth/SeasonList.jsx';
import Season from './pages/Season/Auth/Season.jsx';
import StatAuth from './pages/Stat/StatAuth.jsx';
import { useDomain } from '@useDomain';
import PlayerListAuth from './pages/Player/Auth/PlayerListAuth.jsx';
import TeamStandingsFAB from './pages/Fab/Standings.jsx';

function App() {
  // 🧭 ROUTING INFO
  const location = useLocation();
  const pathname = location.pathname;
  const hostname = window.location.hostname.replace(/^www\./, '');

  // 🧠 AUTH
  const { loading, isAuthenticated } = useAuth();

  // 🏢 ORG STATE
  // const [org, setOrg] = useState(null);
  // const [loadingDomain, setLoadingDomain] = useState(true);
  const { organization: org, loadingDomain } = useDomain();

  // 🔍 ROUTE FLAGS
  const isLoginPage = pathname === "/login";
  const isLocalhost = hostname === "localhost";
  const isSaaSRootDomain = ["sportinghip.com"].includes(hostname);
  const isCustomDomain = !isLocalhost && !isSaaSRootDomain;
  const isSlugRoute = /^\/[a-zA-Z0-9-_]+(\/.*)?$/.test(pathname);
  const isRootPath = pathname === "/";

  // 🌐 VIEW MODE
  const isPublicView = (isCustomDomain || isSlugRoute || isRootPath) && !isAuthenticated;



  useEffect(() => {
    const slugFromPath = location.pathname.split("/")[1];
    const isHomepage =
      location.pathname === "/" || location.pathname === `/${slugFromPath}`;
    document.body.classList.toggle("homepage", isHomepage);
  }, [location.pathname]);

  if (loading || loadingDomain) return null;

  const siteTitle = isCustomDomain ? hostname : org?.slug || "SportingHip";




  return (
    <div className="app_container">
      {!isPublicView && !isLoginPage && <Sidebar />}
      <div className={isPublicView ? "content_wrapper_public" : "content_wrapper"}>
        {org && (
          <Helmet>
            <title>{siteTitle}</title>
            <meta name="description" content={`Welcome to ${siteTitle}`} />
          </Helmet>
        )}
        <Routes>
          {/* ✅ PUBLIC ROUTES */}
          {isPublicView && (
            <Route element={<PublicLayout />}>
              <Route path="/" element={isCustomDomain ? <UserHomepage /> : <Frontend />} />
              <Route path="/site" element={<UserHomepage />} />
              <Route path="/:slug" element={<UserHomepage />} />
              <Route path="/:slug/teamList" element={<TeamList />} />
              <Route path="/:slug/playerList" element={<PlayerList />} />
              <Route path="/:slug/schedule" element={<SchedulePage />} />
              <Route path="/:slug/teams/:id" element={<Team />} />
              <Route path="/:slug/players/:id" element={<Player />} />
              <Route path="/:slug/player/:id/stats" element={<PlayerStats />} />
              <Route path="/:slug/games/:id" element={<GamePage />} />
              <Route path="/:slug/leaderboard" element={<DynamicOrgLeaderboard />} />
              <Route path="/:slug/registration" element={<DynamicOrgRegistration />} />
              <Route path="/:slug/leagueInfo" element={<DynamicOrgLeagueInfo />} />
              <Route path="/:slug/tournament" element={<DynamicOrgTournament />} />
              <Route path="/:slug/ourTeam" element={<DynamicOrgOurTeam />} />
              <Route path="/:slug/leagueFootage" element={<DynamicOrgLeagueFootage />} />
              <Route path="/:slug/players" element={<DynamicOrgPlayers />} />
              <Route path="/:slug/games" element={<DynamicOrgGames />} />
              <Route path="/:slug/youth" element={<DynamicOrgYouth />} />
              <Route path="/:slug/contact" element={<DynamicOrgContact />} />
              <Route path="/:slug/player/:id/stats" element={<PlayerStats />} />
              <Route path="/player/:id/stats" element={<PlayerStats />} />
              <Route path="/schedule" element={<SchedulePage />} />
              <Route path="/teamList" element={<TeamList />} />
              <Route path="/playerList" element={<PlayerList />} />
              <Route path="/playerListAuth" element={<PlayerListAuth />} />
              <Route path="/teams/:id" element={<Team />} />
              <Route path="/players/:id" element={<Player />} />
              <Route path="/games/:id" element={<GamePage />} />
              <Route path="/registration" element={<DynamicOrgRegistration />} />
              <Route path="/leaderboard" element={<DynamicOrgLeaderboard />} />
              <Route path="/leagueInfo" element={<DynamicOrgLeagueInfo />} />
              <Route path="/tournament" element={<DynamicOrgTournament />} />
              <Route path="/ourTeam" element={<DynamicOrgOurTeam />} />
              <Route path="/leagueFootage" element={<DynamicOrgLeagueFootage />} />
              <Route path="/youth" element={<DynamicOrgYouth />} />
              <Route path="/contact" element={<DynamicOrgContact />} />
              <Route path="/login" element={<Login />} />
              <Route path="/:slug/login" element={<Login />} />
            </Route>
          )}

          {/* ✅ AUTHENTICATED ROUTES */}
          {!isPublicView && (
            <>
              <Route path="/organizations" element={<Organizations />} />
              <Route path="/leagueList" element={<LeagueList />} />
              <Route path="/leagues/:id" element={<League />} />
              <Route path="/teamList" element={<TeamList />} />
              <Route path="/teams/:id" element={<Team />} />
              <Route path="/playerListAuth" element={<PlayerList />} />
              <Route path="/players/:id" element={<Player />} />
              <Route path="/player/:id/stats" element={<PlayerStats />} />
              <Route path="/schedule" element={<SchedulePage />} />
              <Route path="/games/:id" element={<GamePage />} />
              <Route path="/stats" element={<StatAuth />} />
              <Route path="/gamePeriod" element={<GamePeriod />} />
              <Route path="/userSettings" element={<UserSettings />} />
              <Route path="/playerAttributes" element={<PlayerAttributes />} />
              <Route path="/season/:seasonId" element={<Season />} />
              <Route path="/dashboard/seasons/:season_id/leagues/:league_id/teams/:team_id" element={<TeamAuth />} />
              <Route path="/dashboard/seasons" element={<Seasons />} />
              <Route path="/dashboard/seasons/:id" element={<Season />} />
              <Route path="/fab/standings" element={<TeamStandingsFAB />} />
              <Route path="/dashboard/seasons/:season_id/leagues/:id" element={<League />} />
              <Route path="/scheduleBuilder" element={<ScheduleBuilder />} />
              <Route path="/businessPage" element={<BusinessPage />} />
            </>
          )}
        </Routes>
      </div>
    </div>
  );
}

export default App;
